/* eslint-disable no-console */
import React, { useState } from 'react';
import {
  Box,
  Button,
  Center,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
  VStack,
  useBoolean,
  Badge,
  FormControl,
  FormLabel,
  Textarea,
  Switch,
  NumberInput,
  NumberInputField,
  Flex,
  Select,
} from '@chakra-ui/react';

import { AddIcon, EditIcon, SearchIcon, ViewIcon } from '@chakra-ui/icons';
import { ColumnDef } from '@tanstack/react-table';
import { useMutation, useQuery } from 'react-query';
import { Select as ReactSelect, MultiValue } from 'chakra-react-select';
import { Api } from 'api';
import { DataTable, LoadingIndicator } from 'components';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment';
import { ExpertisePointQueryCacheKey, ExpertisePointGroupType, ExpertisePointGroupTypeLabel } from 'types';
import { Campaign, CampaignQueryCacheKey, SaveCampaignRequest } from 'types/campaign.types';
import { t, Trans } from '@lingui/macro';
import { AdminPageTitle } from '../components';

interface CampaignFormData {
  id?: number;
  campaignName: string;
  campaignCode: string;
  description: string;
  discountRate: number;
  startDate: string;
  endDate: string;
  isActive: boolean;
  totalUsageLimit: number | null;
  perUserUsageLimit: number | null;
  selectedExpertisePoints: number[];
  termsAndConditions: string;
  groupType: ExpertisePointGroupType | '';
}

const initialFormData: CampaignFormData = {
  campaignName: '',
  campaignCode: '',
  description: '',
  discountRate: 0,
  startDate: '',
  endDate: '',
  isActive: true,
  totalUsageLimit: null,
  perUserUsageLimit: null,
  selectedExpertisePoints: [],
  termsAndConditions: '',
  groupType: '',
};

export const AdminCampaignManagementPage: React.FC = () => {
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBrandFilter, setSelectedBrandFilter] = useState<number | null>(() => {
    const saved = localStorage.getItem('selectedExpertisePointGroupType');
    return saved ? parseInt(saved, 10) : null;
  });
  const [focusedCampaign, setFocusedCampaign] = useState<Campaign | null>(null);
  const [formData, setFormData] = useState<CampaignFormData>(initialFormData);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);

  const { isOpen: isViewModalOpen, onOpen: onViewModalOpen, onClose: onViewModalClose } = useDisclosure();
  const { isOpen: isFormModalOpen, onOpen: onFormModalOpen, onClose: onFormModalClose } = useDisclosure();

  const [isSaving, { on: setSavingOn, off: setSavingOff }] = useBoolean();

  // Listen for brand filter changes from AdminDrawer
  React.useEffect(() => {
    const handleBrandChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const newBrandValue = customEvent.detail.value;
      setSelectedBrandFilter(newBrandValue ? parseInt(newBrandValue, 10) : null);
    };

    window.addEventListener('expertisePointGroupTypeChanged', handleBrandChange);
    return () => {
      window.removeEventListener('expertisePointGroupTypeChanged', handleBrandChange);
    };
  }, []);

  // Form işlemleri
  const resetForm = () => {
    setFormData(initialFormData);
    setEditingCampaign(null);
  };

  // API sorguları
  const {
    data: campaigns,
    isLoading,
    refetch,
  } = useQuery(
    CampaignQueryCacheKey.GetCampaignList,
    async () => {
      const response = await Api.Campaign.getCampaignList();
      return response.data.campaigns;
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 0,
    },
  );

  const { data: expertisePoints } = useQuery(
    ExpertisePointQueryCacheKey.GetAllExpertisePoints,
    async () => {
      const response = await Api.ExpertisePoint.getAllExpertisePoints();
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  // Kampanya detay sorgusu
  const { data: campaignDetail, isLoading: isDetailLoading } = useQuery(
    ['campaign-detail', focusedCampaign?.id],
    async () => {
      if (!focusedCampaign?.id) return null;
      const response = await Api.Campaign.getCampaignDetail(focusedCampaign.id);
      return response.data;
    },
    {
      enabled: !!focusedCampaign?.id && isViewModalOpen,
      refetchOnWindowFocus: false,
    },
  );

  // Kampanya düzenleme detay sorgusu
  const { data: editCampaignDetail, isLoading: isEditDetailLoading } = useQuery(
    ['campaign-edit-detail', editingCampaign?.id],
    async () => {
      if (!editingCampaign?.id) return null;
      const response = await Api.Campaign.getCampaignDetail(editingCampaign.id);
      return response.data;
    },
    {
      enabled: !!editingCampaign?.id && isFormModalOpen,
      refetchOnWindowFocus: false,
    },
  );

  // Kampanya kaydetme mutasyonu
  const saveMutation = useMutation<void, void, SaveCampaignRequest>(
    async (request: SaveCampaignRequest) => {
      await Api.Campaign.saveCampaign(request);
    },
    {
      onMutate: () => {
        setSavingOn();
      },
      onSuccess: () => {
        toast({
          title: editingCampaign ? t`Kampanya başarıyla güncellendi` : t`Kampanya başarıyla oluşturuldu`,
          status: 'success',
        });
        refetch();
        onFormModalClose();
        resetForm();
      },
      onError: () => {
        toast({
          title: t`Kampanya kaydedilirken hata oluştu`,
          status: 'error',
        });
      },
      onSettled: () => {
        setSavingOff();
      },
    },
  );

  // Kampanya silme mutasyonu
  /* const deleteMutation = useMutation<void, void, number>(
    async (campaignId: number) => {
      await Api.Campaign.deleteCampaign(campaignId);
    },
    {
      onMutate: () => {
        setDeletingOn();
      },
      onSuccess: () => {
        toast({
          title: t`Kampanya başarıyla silindi`,
          status: 'success',
        });
        refetch();
      },
      onError: () => {
        toast({
          title: t`Kampanya silinirken hata oluştu`,
          status: 'error',
        });
      },
      onSettled: () => {
        setDeletingOff();
      },
    },
  ); */

  // Form işlemleri

  const openAddModal = () => {
    resetForm();
    onFormModalOpen();
  };

  const openEditModal = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    onFormModalOpen();
  };

  // Form verilerini API'den gelen detay verilerle güncelle
  React.useEffect(() => {
    if (editCampaignDetail && editingCampaign) {
      setFormData({
        id: editCampaignDetail.id,
        campaignName: editCampaignDetail.campaignName || editCampaignDetail.name || '',
        campaignCode: editCampaignDetail.campaignCode || editCampaignDetail.code || '',
        description: editCampaignDetail.description || '',
        discountRate: editCampaignDetail.discountRate || 0,
        startDate: editCampaignDetail.startDate ? moment(editCampaignDetail.startDate).format('YYYY-MM-DD') : '',
        endDate: editCampaignDetail.endDate ? moment(editCampaignDetail.endDate).format('YYYY-MM-DD') : '',
        isActive: editCampaignDetail.isActive,
        totalUsageLimit: editCampaignDetail.usageLimit || null,
        perUserUsageLimit: editCampaignDetail.perUserUsageLimit || null,
        selectedExpertisePoints: editCampaignDetail.expertisePoints?.map((ep) => ep.id) || [],
        termsAndConditions: editCampaignDetail.termsAndConditions || '',
        groupType: editCampaignDetail.groupType || ExpertisePointGroupType.OccoExpertise,
      });
    }
  }, [editCampaignDetail, editingCampaign]);

  const handleFormSubmit = () => {
    if (!formData.campaignName.trim()) {
      toast({
        title: t`Kampanya adı gereklidir`,
        status: 'error',
      });
      return;
    }

    if (!formData.campaignCode.trim()) {
      toast({
        title: t`Kampanya kodu gereklidir`,
        status: 'error',
      });
      return;
    }

    if (formData.groupType === '') {
      toast({
        title: t`Marka seçimi gereklidir`,
        status: 'error',
      });
      return;
    }

    if (!formData.startDate || !formData.endDate) {
      toast({
        title: t`Başlangıç ve bitiş tarihleri gereklidir`,
        status: 'error',
      });
      return;
    }

    const request: SaveCampaignRequest = {
      id: editingCampaign?.id,
      campaignName: formData.campaignName,
      campaignCode: formData.campaignCode,
      description: formData.description,
      discountRate: formData.discountRate,
      startDate: new Date(formData.startDate),
      endDate: new Date(formData.endDate),
      totalUsageLimit: formData.totalUsageLimit ?? undefined,
      perUserUsageLimit: formData.perUserUsageLimit ?? undefined,
      expertisePoints: formData.selectedExpertisePoints,
      termsAndConditions: formData.termsAndConditions,
      isActive: formData.isActive,
      groupType: formData.groupType as ExpertisePointGroupType,
    };

    saveMutation.mutate(request);
  };

  const handleInputChange = (field: keyof CampaignFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const columns: ColumnDef<Campaign>[] = [
    {
      header: t`Kampanya Adı`,
      accessorKey: 'name',
      cell: (info) => <Text>{info.getValue() as string}</Text>,
    },
    {
      header: t`Marka`,
      accessorKey: 'groupType',
      cell: (info) => {
        const groupType = info.getValue() as number;
        const brandName = ExpertisePointGroupTypeLabel.get(Number(groupType)) || 'Bilinmeyen';
        return <Text>{brandName}</Text>;
      },
    },
    {
      header: t`Durum`,
      accessorKey: 'isActive',
      cell: (info) => (
        <Badge colorScheme={(info.getValue() as boolean) ? 'green' : 'red'} variant="subtle">
          {(info.getValue() as boolean) ? <Trans>Aktif</Trans> : <Trans>Pasif</Trans>}
        </Badge>
      ),
    },
    {
      header: t`İndirim`,
      accessorKey: 'discountRateStr',
      cell: (info) => {
        return (
          <Badge colorScheme={info.getValue() ? 'blue' : 'gray'} variant="outline">
            {info.getValue() as string}
          </Badge>
        );
      },
    },
    {
      header: t`Geçerlilik Süresi`,
      accessorKey: 'endDate',
      cell: (info) => <Text>{moment(info.getValue() as string).format('DD.MM.YYYY')}</Text>,
    },
    {
      id: 'actions',
      header: t`İşlemler`,
      cell: (cellProps) => {
        const { row } = cellProps;
        const campaign = row.original;

        return (
          <HStack spacing={1}>
            <IconButton
              aria-label={t`Görüntüle`}
              icon={<ViewIcon />}
              size="sm"
              colorScheme="blue"
              variant="ghost"
              onClick={() => {
                setFocusedCampaign(campaign);
                onViewModalOpen();
              }}
            />
            <IconButton
              aria-label={t`Düzenle`}
              icon={<EditIcon />}
              size="sm"
              colorScheme="orange"
              variant="ghost"
              onClick={() => openEditModal(campaign)}
            />
            {/* <IconButton
              aria-label={t`Sil`}
              icon={<DeleteIcon />}
              size="sm"
              colorScheme="red"
              variant="ghost"
              isLoading={isDeleting}
              onClick={() => {
                // eslint-disable-next-line no-alert
                if (window.confirm(t`Bu kampanyayı silmek istediğinizden emin misiniz?`)) {
                  deleteMutation.mutate(campaign.id);
                  refetch();
                }
              }}
            /> */}
          </HStack>
        );
      },
    },
  ];

  // Arama ve marka filtreleme fonksiyonu
  const filteredCampaigns = React.useMemo(() => {
    if (!campaigns) return [];

    let filtered = campaigns;

    // Apply brand filter
    if (selectedBrandFilter !== null) {
      filtered = filtered.filter((campaign) => campaign.groupType === selectedBrandFilter);
    }

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter((campaign) => campaign.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }

    return filtered;
  }, [campaigns, searchQuery, selectedBrandFilter]);

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <>
      <AdminPageTitle title={t`Kampanya Listesi`} />

      {/* Kampanya Form Modal */}
      <Modal isOpen={isFormModalOpen} onClose={onFormModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingCampaign ? <Trans>Kampanya Düzenle</Trans> : <Trans>Kampanya Yönetimi</Trans>}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isEditDetailLoading && editingCampaign ? (
              <Center py={8}>
                <LoadingIndicator />
              </Center>
            ) : (
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel>
                    <Trans>Marka</Trans>
                  </FormLabel>
                  <Select
                    value={formData.groupType}
                    onChange={(e) => {
                      const newGroupType = e.target.value ? parseInt(e.target.value, 10) : '';
                      handleInputChange('groupType', newGroupType);
                      // Clear selected expertise points when brand changes
                      handleInputChange('selectedExpertisePoints', []);
                    }}
                  >
                    <option value="">{t`Marka seçiniz`}</option>
                    {Array.from(ExpertisePointGroupTypeLabel.entries()).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>
                    <Trans>Kampanya Adı</Trans>
                  </FormLabel>
                  <Input
                    value={formData.campaignName}
                    onChange={(e) => handleInputChange('campaignName', e.target.value)}
                    placeholder={t`Kampanya adını giriniz`}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>
                    <Trans>Kampanya Kodu</Trans>
                  </FormLabel>
                  <Input
                    value={formData.campaignCode}
                    onChange={(e) => handleInputChange('campaignCode', e.target.value)}
                    placeholder={t`Kampanya kodunu giriniz`}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>
                    <Trans>Açıklama</Trans>
                  </FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder={t`Kampanya açıklamasını giriniz`}
                    rows={3}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>
                    <Trans>İndirim Oranı (%)</Trans>
                  </FormLabel>
                  <NumberInput
                    value={formData.discountRate}
                    onChange={(valueString) => handleInputChange('discountRate', parseFloat(valueString) || 0)}
                    min={0}
                    max={100}
                  >
                    <NumberInputField placeholder="10" />
                  </NumberInput>
                </FormControl>

                <HStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>
                      <Trans>Geçerlilik Başlangıç Tarihi</Trans>
                    </FormLabel>
                    <Input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => handleInputChange('startDate', e.target.value)}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>
                      <Trans>Geçerlilik Bitiş Tarihi</Trans>
                    </FormLabel>
                    <Input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => handleInputChange('endDate', e.target.value)}
                    />
                  </FormControl>
                </HStack>

                <Flex justify="space-between" align="center">
                  <FormLabel mb={0}>
                    <Trans>Aktif</Trans>
                  </FormLabel>
                  <Switch
                    isChecked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    colorScheme="blue"
                    size="lg"
                  />
                </Flex>

                <FormControl>
                  <FormLabel>
                    <Trans>Toplam Kullanım Limiti</Trans>
                  </FormLabel>
                  <NumberInput
                    value={formData.totalUsageLimit || ''}
                    onChange={(valueString) =>
                      handleInputChange('totalUsageLimit', valueString ? parseInt(valueString, 10) : null)
                    }
                    min={0}
                  >
                    <NumberInputField placeholder={t`Sınırsız için boş bırakın`} />
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>
                    <Trans>Kullanıcı Bazlı Kullanım Limiti</Trans>
                  </FormLabel>
                  <NumberInput
                    value={formData.perUserUsageLimit || ''}
                    onChange={(valueString) =>
                      handleInputChange('perUserUsageLimit', valueString ? parseInt(valueString, 10) : null)
                    }
                    min={0}
                  >
                    <NumberInputField placeholder={t`Sınırsız için boş bırakın`} />
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>
                    <Trans>Geçerli Bayiler / Lokasyonlar</Trans>
                  </FormLabel>
                  <ReactSelect
                    isMulti
                    isDisabled={formData.groupType === ''}
                    options={
                      expertisePoints
                        ?.filter((point) => formData.groupType === '' || point.groupType === formData.groupType)
                        ?.map((point) => ({
                          label: point.name,
                          value: point.id,
                        })) || []
                    }
                    value={formData.selectedExpertisePoints.map((id) => ({
                      label: expertisePoints?.find((p) => p.id === id)?.name || '',
                      value: id,
                    }))}
                    onChange={(selectedOptions: MultiValue<{ label: string; value: number }>) => {
                      const selectedIds = selectedOptions.map((option) => option.value);
                      handleInputChange('selectedExpertisePoints', selectedIds);
                    }}
                    placeholder={formData.groupType === '' ? t`Önce marka seçiniz` : t`Bayileri seçiniz`}
                    closeMenuOnSelect={false}
                    isClearable
                    noOptionsMessage={() => t`Bayi bulunamadı`}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>
                    <Trans>Şartlar ve Koşullar</Trans>
                  </FormLabel>
                  <Textarea
                    value={formData.termsAndConditions}
                    onChange={(e) => handleInputChange('termsAndConditions', e.target.value)}
                    placeholder={t`Kampanya şartları ve koşullarını giriniz`}
                    rows={4}
                  />
                </FormControl>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onFormModalClose}>
              <Trans>İptal</Trans>
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleFormSubmit}
              isLoading={isSaving}
              isDisabled={Boolean(isEditDetailLoading && editingCampaign)}
            >
              {editingCampaign ? <Trans>Güncelle</Trans> : <Trans>Oluştur</Trans>}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Kampanya Detay Modal */}
      <Modal isOpen={isViewModalOpen} onClose={onViewModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Kampanya Detayları</Trans>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isDetailLoading && (
              <Center py={8}>
                <LoadingIndicator />
              </Center>
            )}
            {!isDetailLoading && campaignDetail && (
              <VStack align="start" spacing={4}>
                <Box>
                  <Text fontWeight="bold" mb={1}>
                    <Trans>Kampanya Adı:</Trans>
                  </Text>
                  <Text>{campaignDetail.campaignName}</Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" mb={1}>
                    <Trans>Kampanya Kodu:</Trans>
                  </Text>
                  <Text>{campaignDetail.campaignCode || '-'}</Text>
                </Box>

                {campaignDetail.description && (
                  <Box>
                    <Text fontWeight="bold" mb={1}>
                      <Trans>Açıklama:</Trans>
                    </Text>
                    <Text>{campaignDetail.description}</Text>
                  </Box>
                )}

                <Box>
                  <Text fontWeight="bold" mb={1}>
                    <Trans>Durum:</Trans>
                  </Text>
                  <Badge colorScheme={campaignDetail.isActive ? 'green' : 'red'}>
                    {campaignDetail.isActive ? <Trans>Aktif</Trans> : <Trans>Pasif</Trans>}
                  </Badge>
                </Box>

                <Box>
                  <Text fontWeight="bold" mb={1}>
                    <Trans>İndirim Oranı:</Trans>
                  </Text>
                  <Badge colorScheme="blue" variant="outline">
                    {campaignDetail.discountRateStr || 0}
                  </Badge>
                </Box>

                <HStack spacing={4}>
                  <Box>
                    <Text fontWeight="bold" mb={1}>
                      <Trans>Başlangıç Tarihi:</Trans>
                    </Text>
                    <Text>{moment(campaignDetail.startDate).format('DD.MM.YYYY')}</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold" mb={1}>
                      <Trans>Bitiş Tarihi:</Trans>
                    </Text>
                    <Text>{moment(campaignDetail.endDate).format('DD.MM.YYYY')}</Text>
                  </Box>
                </HStack>

                {campaignDetail.usageLimit && (
                  <Box>
                    <Text fontWeight="bold" mb={1}>
                      <Trans>Toplam Kullanım Limiti:</Trans>
                    </Text>
                    <Text>{campaignDetail.usageLimit}</Text>
                  </Box>
                )}

                {campaignDetail.perUserUsageLimit && (
                  <Box>
                    <Text fontWeight="bold" mb={1}>
                      <Trans>Kullanıcı Bazlı Kullanım Limiti:</Trans>
                    </Text>
                    <Text>{campaignDetail.perUserUsageLimit}</Text>
                  </Box>
                )}

                {campaignDetail.expertisePoints && campaignDetail.expertisePoints.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>
                      <Trans>Atanmış Bayiler:</Trans>
                    </Text>
                    <VStack align="start" spacing={1}>
                      {campaignDetail.expertisePoints.map((point) => (
                        <Text key={point.id} fontSize="sm">
                          {point.name}
                        </Text>
                      ))}
                    </VStack>
                  </Box>
                )}

                {campaignDetail.termsAndConditions && (
                  <Box>
                    <Text fontWeight="bold" mb={1}>
                      <Trans>Şartlar ve Koşullar:</Trans>
                    </Text>
                    <Text fontSize="sm" whiteSpace="pre-wrap">
                      {campaignDetail.termsAndConditions}
                    </Text>
                  </Box>
                )}
              </VStack>
            )}
            {!isDetailLoading && !campaignDetail && (
              <Center py={8}>
                <Text color="gray.500">
                  <Trans>Kampanya detayları yüklenemedi</Trans>
                </Text>
              </Center>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onViewModalClose}>
              <Trans>Kapat</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Ana İçerik */}
      <Box>
        {/* Search and Add Button - Always Visible */}
        <HStack spacing={4} justifyContent="space-between" w="100%" mb={4}>
          <InputGroup maxW="300px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder={t`Ne aramıştınız?`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </InputGroup>

          <Button colorScheme="green" leftIcon={<AddIcon />} onClick={openAddModal}>
            <Trans>Yeni Kampanya</Trans>
          </Button>
        </HStack>

        {/* Campaign List or Empty State */}
        {!isLoading && filteredCampaigns.length === 0 && !searchQuery && (
          <Center py={10}>
            <Text fontSize="lg" color="gray.500">
              <Trans>Henüz kampanya bulunmamaktadır.</Trans>
            </Text>
          </Center>
        )}

        {!isLoading && filteredCampaigns.length === 0 && searchQuery && (
          <Center py={10}>
            <Text fontSize="lg" color="gray.500">
              <Trans>Arama kriterlerinize uygun kampanya bulunamadı.</Trans>
            </Text>
          </Center>
        )}

        {filteredCampaigns.length > 0 && <DataTable data={filteredCampaigns} columns={columns} usePagination />}
      </Box>
    </>
  );
};
